import { EventEmitter, TemplateRef } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import { EnumHelper, ModalService, ModalSettings } from 'mbs-ui-kit';
import { EnumToSelectItem } from 'mbs-ui-kit/utils/enum-helper';
import { AzureAccessTierType, StorageClass, StorageType, TypesForFFI } from 'mbs-ui-shared@models';
import { BehaviorSubject } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { AdvancedOptionsStepValue } from 'wizards/models/advanced-options-models';
import { AdvancedSettingsVirtualStepValue, VMQuiesceType } from 'wizards/models/advanced-settings-vm-models';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { AgentVersions } from 'wizards/steps/StepBase.class';

export interface AdvancedOptionsStepLogicHook {
  isNBF: boolean;
  isUnix: boolean;
  isRDMode: boolean;
  stepFocused: boolean;
  isBackupPlan: boolean;
  userInteractWithSteps: boolean;
  selectedStorageType: StorageType;

  agentHigherThat: (version: AgentVersions) => boolean;
  agentHigherOrEqualThat: (version: AgentVersions) => boolean;
  toggleFormControls: (controlNames: string[], isEnabled: boolean) => void;
  recalculateSyntheticFullVisible: (
    storageType: StorageType,
    fStorageClass: StorageClass,
    fAzureAccessTierType: AzureAccessTierType,
    syntheticFullField: string
  ) => void;

  restoreVerificationEnabled?: EventEmitter<unknown>;
  changeStorageType: EventEmitter<{
    azureType?: AzureAccessTierType;
    isStorageBadForFFI: boolean;
    storageClass: StorageClass;
  }>;
}

export class AdvancedOptionsStepLogic {
  public readonly expensiveStorageClasses: { S3: Array<StorageClass>; Azure: Array<AzureAccessTierType> } = {
    S3: [StorageClass.INTELLIGENT_TIERING, StorageClass.GLACIER_IR, StorageClass.STANDARD_IA, StorageClass.ONEZONE_IA],
    Azure: [AzureAccessTierType.Cool]
  };
  private readonly defaultValuesMap = {
    PrefetchBlockCount: { value: 0 },
    BlockSize: { value: 0 },
    excludeRules: { value: [] },
    SyntheticFull: { value: true }
  };

  public AccessTires: Array<EnumToSelectItem & { description1: string; description2: string }>;
  public StorageClasses: Array<EnumToSelectItem & { description1: string; description2: string }>;
  public needValidateStorageClassOrAzure = false;

  private stepForm: FormGroup;
  private AccessTiresDescriptions = {
    [AzureAccessTierType.Hot]: this.i18nPipe.transform('wizards:access_tier_hot_label_tooltip_desc', { returnObjects: true }),
    [AzureAccessTierType.Cool]: this.i18nPipe.transform('wizards:access_tier_cool_label_tooltip_desc', { returnObjects: true }),
    [AzureAccessTierType.Cold]: this.i18nPipe.transform('wizards:access_tier_cold_label_tooltip_desc', { returnObjects: true }),
    [AzureAccessTierType.Archive]: this.i18nPipe.transform('wizards:access_tier_archive_label_tooltip_desc', { returnObjects: true })
  };
  private StorageClassesDescriptions = {
    [StorageClass.STANDARD]: this.i18nPipe.transform('wizards:s3_standard_label_tooltip_desc', { returnObjects: true }),
    [StorageClass.STANDARD_IA]: this.i18nPipe.transform('wizards:s3_standard_ia_label_tooltip_desc', { returnObjects: true }),
    [StorageClass.INTELLIGENT_TIERING]: this.i18nPipe.transform('wizards:s3_it_label_tooltip_desc', { returnObjects: true }),
    [StorageClass.ONEZONE_IA]: this.i18nPipe.transform('wizards:s3_one_zone_label_tooltip_desc', { returnObjects: true }),
    [StorageClass.GLACIER]: this.i18nPipe.transform('wizards:s3_glacier_flex_label_tooltip_desc', { returnObjects: true }),
    [StorageClass.GLACIER_IR]: this.i18nPipe.transform('wizards:s3_glacier_instant_label_tooltip_desc', { returnObjects: true }),
    [StorageClass.DEEP_ARCHIVE]: this.i18nPipe.transform('wizards:s3_glacier_deep_label_tooltip_desc', { returnObjects: true })
  };
  private readonly syntheticFullModalSettings: ModalSettings = {
    header: { title: this.i18nPipe.transform('wizards:error_synthetic_title', { format: 'title' }) },
    footer: {
      okButton: { text: this.i18nPipe.transform('shared:buttons.yes', { format: 'title' }), type: 'success' },
      cancelButton: { text: this.i18nPipe.transform('shared:buttons.no', { format: 'title' }) }
    }
  };

  constructor(
    private modalService: ModalService,
    private stepService: WizardStepsService,
    private i18nPipe: I18NextPipe,
    private hooks: AdvancedOptionsStepLogicHook
  ) {
    this.AccessTires = this.prepareDictionary(
      EnumHelper.EnumToSelectIndexesArray(AzureAccessTierType, null, [
        AzureAccessTierType.Hot,
        AzureAccessTierType.Cool,
        AzureAccessTierType.Cold,
        AzureAccessTierType.Archive
      ]),
      this.AccessTiresDescriptions
    );
    this.StorageClasses = this.prepareDictionary(StepsHelpers.getStorageClasses(), this.StorageClassesDescriptions);

    if (
      this.hooks.isRDMode ||
      this.hooks.agentHigherOrEqualThat(AgentVersions.GlacierIR) ||
      (this.hooks.isUnix && this.hooks.agentHigherOrEqualThat(AgentVersions.GlacierIRLinux))
    ) {
      this.StorageClasses = this.prepareDictionary(StepsHelpers.getStorageClasses(true), this.StorageClassesDescriptions);
    }

    let removeAzureCold = false;

    if (!this.hooks.isRDMode) {
      removeAzureCold = !this.hooks.agentHigherOrEqualThat(this.hooks.isUnix ? AgentVersions.AzureColdLinux : AgentVersions.AzureCold);
    }

    if (removeAzureCold) {
      this.AccessTires = this.AccessTires.filter((item) => item.value !== AzureAccessTierType.Cold);
    }
  }

  getForm(type: 'full' | 'vm'): FormGroup {
    if (type === 'vm') {
      this.stepForm = new UntypedFormGroup({
        quiesceType: new FormControl(VMQuiesceType.TryApplicationConsistent),
        useChangeBlockTracking: new FormControl(false),
        useS3Acceleration: new FormControl(false),
        storageClass: new FormControl(1),
        azureAccessTier: new FormControl(0),
        syntheticFull: new FormControl(false)
      });
    }

    if (type === 'full') {
      this.stepForm = new UntypedFormGroup({
        UseS3Acceleration: new FormControl(false),
        StorageClass: new FormControl(1),
        azureAccessTier: new FormControl(0),
        ExcludeEnabled: new FormControl(false),
        excludeRules: new FormControl([], [Validators.required]),
        SyntheticFull: new FormControl(false),
        SaveDeletedDataInCloud: new FormControl(false),
        IgnoreBadSectors: new FormControl(false),
        DisableVSS: new FormControl(false),
        useSystemVSSProvider: new FormControl(false),
        KeepEFSFilesAsIs: new FormControl(false),
        PrefetchBlockCount: new FormControl(0),
        BlockSize: new FormControl(),
        AlwaysUseVSS: new FormControl(false),
        BackupNTFSPermissions: new FormControl(false),
        UseFastNtfsScan: new FormControl(false)
      });
    }

    return this.stepForm;
  }

  resetStorageClassesForRD(): void {
    this.StorageClasses = this.prepareDictionary(StepsHelpers.getStorageClasses(true), this.StorageClassesDescriptions);
  }

  storageClassChangeHandler(sClass: StorageClass, vm: boolean): void {
    const storageClass = this.stepForm.get(vm ? 'storageClass' : 'StorageClass');
    const syntheticFull = this.stepForm.get(vm ? 'syntheticFull' : 'SyntheticFull');
    const isSyntheticFull = syntheticFull.value;

    const final = () => {
      this.needValidateStorageClassOrAzure = TypesForFFI.unsupported.Amazon.includes(sClass);
      this.hooks.restoreVerificationEnabled?.emit(!this.needValidateStorageClassOrAzure);
      syntheticFull.updateValueAndValidity();
      this.hooks.changeStorageType.emit({
        isStorageBadForFFI: StepsHelpers.isStorageBadForFFI(sClass, 'Amazon'),
        storageClass: sClass
      });
    };

    if (!this.hooks.isUnix && isSyntheticFull && this.hooks.stepFocused && this.expensiveStorageClasses.S3.includes(sClass)) {
      storageClass.setValue(StorageClass.STANDARD);

      return void AdvancedOptionsStepLogic.showStorageCostImpact(
        String(this.StorageClasses.find((item) => item.value === StorageClass.STANDARD).label),
        this.i18nPipe,
        this.modalService
      )
        .then((result) => {
          if (result) {
            storageClass.setValue(sClass);
          }
        })
        .finally(final);
    }

    final();
  }

  azureAccessTierChangeHandler(type: AzureAccessTierType, vm: boolean): void {
    const azureAccessTier = this.stepForm.get('azureAccessTier');
    const storageClass = this.stepForm.get(vm ? 'storageClass' : 'StorageClass');
    const syntheticFull = this.stepForm.get(vm ? 'syntheticFull' : 'SyntheticFull');
    const isSyntheticFull = syntheticFull.value;

    const final = () => {
      this.needValidateStorageClassOrAzure = type === AzureAccessTierType.Archive;
      this.hooks.restoreVerificationEnabled?.emit(type !== AzureAccessTierType.Archive);
      syntheticFull.updateValueAndValidity();
      this.hooks.changeStorageType.emit({
        azureType: type,
        isStorageBadForFFI: StepsHelpers.isStorageBadForFFI(type, 'Azure'),
        storageClass: storageClass.value
      });
    };

    if (!this.hooks.isUnix && isSyntheticFull && this.hooks.stepFocused && this.expensiveStorageClasses.Azure.includes(type)) {
      azureAccessTier.setValue(AzureAccessTierType.Hot);

      return void AdvancedOptionsStepLogic.showStorageCostImpact(
        String(this.AccessTires.find((item) => item.value === AzureAccessTierType.Hot).label),
        this.i18nPipe,
        this.modalService
      )
        .then((result) => {
          if (result) {
            azureAccessTier.setValue(type);
          }
        })
        .finally(final);
    }

    final();
  }

  setValuesFromObjOrDefault(value: Pick<AdvancedOptionsStepValue, 'StorageClass' | 'azureAccessTier'>, isCreate: boolean, vm: false): void;
  setValuesFromObjOrDefault(
    value: Pick<AdvancedSettingsVirtualStepValue, 'storageClass' | 'azureAccessTier'>,
    isCreate: boolean,
    vm: true
  ): void;
  setValuesFromObjOrDefault(
    value: Pick<AdvancedOptionsStepValue, 'StorageClass' | 'azureAccessTier'> &
      Pick<AdvancedSettingsVirtualStepValue, 'storageClass' | 'azureAccessTier'>,
    isCreate: boolean,
    vm: boolean
  ): void {
    const azureAccessTier = this.stepForm.get('azureAccessTier');
    const storageClass = this.stepForm.get(vm ? 'storageClass' : 'StorageClass');
    const storageClassValue = vm ? value.storageClass : value.StorageClass;
    const syntheticFull = this.stepForm.get(vm ? 'syntheticFull' : 'SyntheticFull');

    if (!isNil(storageClassValue)) this.storageClassChangeHandler(storageClassValue, vm);
    else storageClass.setValue(1);

    if (!isNil(value.azureAccessTier)) this.azureAccessTierChangeHandler(value.azureAccessTier, vm);
    else azureAccessTier.setValue(0);

    if (this.hooks.isNBF) syntheticFull.enable();
    if (isCreate) syntheticFull.setValue(true);
  }

  initEvents(syntheticFullSettingPossible: BehaviorSubject<boolean>, vm = false): void {
    const syntheticFullField = vm ? 'syntheticFull' : 'SyntheticFull';
    const azureAccessTier = this.stepForm.get('azureAccessTier');
    const storageClass = this.stepForm.get(vm ? 'storageClass' : 'StorageClass');
    const syntheticFull = this.stepForm.get(syntheticFullField);

    const recalculateSyntheticFullVisible = () =>
      this.hooks.recalculateSyntheticFullVisible(
        this.hooks.selectedStorageType,
        storageClass.value,
        azureAccessTier.value,
        syntheticFullField
      );

    syntheticFull.valueChanges
      .pipe(untilDestroyed(this.hooks))
      .subscribe((value) => this.stepService.updateProperty('options', 'syntheticFull', value));

    this.stepService
      .pipeProperty('storage', 'type')
      .pipe(startWith(this.stepService.getSnapshot('storage', 'type')), untilDestroyed(this.hooks))
      .subscribe((value) => (this.hooks.selectedStorageType = value));

    this.stepService
      .pipeProperty('wizard', 'userInteractWithSteps')
      .pipe(untilDestroyed(this.hooks))
      .subscribe((value) => (this.hooks.userInteractWithSteps = value));

    // RXJS merge doesn`t work...
    syntheticFullSettingPossible.pipe(untilDestroyed(this.hooks)).subscribe(recalculateSyntheticFullVisible);
    storageClass.valueChanges.pipe(untilDestroyed(this.hooks)).subscribe(recalculateSyntheticFullVisible);
    azureAccessTier.valueChanges.pipe(untilDestroyed(this.hooks)).subscribe(recalculateSyntheticFullVisible);
    this.stepService.pipeProperty('storage', 'type').pipe(untilDestroyed(this.hooks)).subscribe(recalculateSyntheticFullVisible);
  }

  enableAndSetDefaultValue(key, isNullValue: boolean, isCreate: boolean): void {
    if (isNullValue && key !== 'azureAccessTier' && key !== 'StorageClass') {
      const needValueFromMap = key === 'SyntheticFull' ? isCreate : this.defaultValuesMap[key];
      this.stepForm.get(key).patchValue(needValueFromMap ? this.defaultValuesMap[key].value : false);
    }

    if (key === 'useSystemVSSProvider' && (!this.hooks.isBackupPlan || this.stepForm.get('AlwaysUseVSS').value))
      return void this.hooks.toggleFormControls([key], true);

    if (key === 'AlwaysUseVSS' && this.stepForm.get('UseFastNtfsScan').value) return void this.hooks.toggleFormControls([key], false);

    this.hooks.toggleFormControls([key], true);
  }

  showSyntheticFullModal(errorSyntheticFull: TemplateRef<HTMLElement>): Promise<boolean> {
    return this.modalService.open(this.syntheticFullModalSettings, errorSyntheticFull);
  }

  private prepareDictionary(
    items: Array<EnumToSelectItem>,
    dictionary: Record<AzureAccessTierType, string> | Record<StorageClass, string>
  ): Array<EnumToSelectItem & { description1: string; description2: string }> {
    return items.map((item) => ({ ...item, description1: dictionary[item.value][0], description2: dictionary[item.value][1] }));
  }

  static showStorageCostImpact(standardStorageName: string, i18next: I18NextPipe, modalService: ModalService): Promise<boolean> {
    return modalService.open(
      {
        header: {
          title: i18next.transform('wizards:syntheticFullCostImpact')
        },
        footer: {
          okButton: { text: i18next.transform('wizards:syntheticFullCostImpactApplySettings') }
        }
      },
      i18next
        .transform('wizards:syntheticFullCostImpactText', {
          storageClass: 'StorageClass'
        })
        .replace('StorageClass', `<strong>${standardStorageName}</strong>`)
    );
  }
}
